import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { REMEMBERED_LINES_STORAGE_KEY } from '@theme/cart-remembered-lines-key';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    300
  );
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /**
   * Timestamp of the last render of this section that reflected server-side stock.
   * The initial server render counts, so the clock starts at construction.
   * @type {number}
   */
  #lastValidatedAt = Date.now();

  /** @type {boolean} */
  #revalidating = false;

  /** In-flight `updateQuantity()` calls (a count, as they can overlap); stock revalidation waits for 0. @type {number} */
  #pendingQuantityUpdates = 0;

  /** cart-remembered-lines.js, lazy-loaded when needed (SM-776). @type {typeof import('./cart-remembered-lines.js') | null} */
  #rememberedLines = null;

  /** @type {boolean} */
  #rememberedLinesRequested = false;

  /** @type {MutationObserver | undefined} */
  #rememberedLinesTrigger;

  #loadRememberedLinesIfNeeded = () => {
    if (this.#rememberedLinesRequested) return;

    let remembered = false;
    try {
      remembered = localStorage.getItem(REMEMBERED_LINES_STORAGE_KEY) !== null;
    } catch {
      // Storage blocked: nothing can be remembered.
    }
    if (!remembered && !this.querySelector('[data-sold-out]')) return;

    this.#rememberedLinesRequested = true;
    this.#rememberedLinesTrigger?.disconnect();
    import('@theme/cart-remembered-lines')
      .then((module) => {
        this.#rememberedLines = module;
        module.watch(this);
      })
      .catch((error) => console.warn('[cart-items] Remembered sold-out lines failed to load:', error));
  };

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);

    document.addEventListener('visibilitychange', this.#revalidateStockIfStale);
    window.addEventListener('pageshow', this.#handlePageShow);
    window.addEventListener('focus', this.#revalidateStockIfStale);
    document.addEventListener('pointerdown', this.#revalidateStockIfStale, { passive: true });
    document.addEventListener('keydown', this.#revalidateStockIfStale, { passive: true });

    this.#loadRememberedLinesIfNeeded();
    if (!this.#rememberedLinesRequested) {
      this.#rememberedLinesTrigger = new MutationObserver(this.#loadRememberedLinesIfNeeded);
      this.#rememberedLinesTrigger.observe(this, { childList: true, subtree: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#rememberedLinesTrigger?.disconnect();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);

    document.removeEventListener('visibilitychange', this.#revalidateStockIfStale);
    window.removeEventListener('pageshow', this.#handlePageShow);
    window.removeEventListener('focus', this.#revalidateStockIfStale);
    document.removeEventListener('pointerdown', this.#revalidateStockIfStale);
    document.removeEventListener('keydown', this.#revalidateStockIfStale);
  }

  /**
   * How long a rendered cart may be trusted before its stock data is re-checked,
   * in milliseconds. `0` means the merchant has switched revalidation off.
   * @returns {number}
   */
  get #revalidateInterval() {
    const seconds = Number(this.dataset.revalidateInterval);

    return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 0;
  }

  /**
   * Re-renders the cart section so its Liquid re-evaluates variant availability,
   * flagging any line that has sold out since the cart was last rendered.
   *
   * Deliberately triggered on *resume* (tab becomes visible, window refocused,
   * bfcache restore, first interaction) rather than on an interval: a poll that
   * fires while nobody is looking costs a request and changes nothing the
   * shopper can see. Skipped while the shopper is typing in the cart or a cart
   * mutation is in flight, so a background render never clobbers their input.
   */
  #revalidateStockIfStale = () => {
    const interval = this.#revalidateInterval;

    if (!interval || Shopify.designMode) return;
    if (this.#revalidating || this.#pendingCartFetch || this.#pendingQuantityUpdates > 0) return;
    // An empty cart has no stock to go stale — and most visitors carry one.
    if (!this.refs.cartItemRows?.length) return;
    if (document.visibilityState !== 'visible') return;
    if (Date.now() - this.#lastValidatedAt < interval) return;

    // Never morph out from under a field the shopper is using.
    const { activeElement } = document;
    if (activeElement instanceof HTMLElement && activeElement !== document.body && this.contains(activeElement)) {
      return;
    }

    this.#revalidating = true;
    this.#lastValidatedAt = Date.now();

    sectionRenderer
      .renderSection(this.sectionId, { cache: false, mode: this.isDrawer ? 'hydration' : 'full' })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Stock revalidation failed:', error);
      })
      .finally(() => {
        this.#revalidating = false;
      });
  };

  /**
   * A bfcache restore hands back markup rendered before the shopper left, so the
   * staleness clock has to be reset to when that render actually happened.
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    if (!event.persisted) return;

    this.#lastValidatedAt = 0;
    this.#revalidateStockIfStale();
  };

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    let { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    // Keyed from the row used, now: by the debounced update a logged-in cart may have
    // dropped a sold-out line above it and renumbered the rows (SM-776).
    const lineKey = this.#rowKeyFor(event.target);

    if (quantity === 0) {
      return this.#removeLine(line, lineKey);
    }

    // update.js enforces no stock, so a request past the stock left (data-stock-max,
    // quantity-selector.liquid) is clamped here, with the message Shopify's change.js
    // gave: "Only N item(s) were added to your cart due to availability" (SM-776).
    const input = event.target instanceof Element ? event.target.querySelector('input[data-stock-max]') : null;
    const stockMax = Number(input?.dataset.stockMax);
    let onlyAdded;
    if (input instanceof HTMLInputElement && stockMax > 0 && quantity > stockMax) {
      quantity = onlyAdded = stockMax;
      input.value = String(stockMax);
      if (Number(input.defaultValue) === stockMax) return this.#showLineMessage(lineKey, this.#onlyAddedText(stockMax));
    }

    this.updateQuantity({
      line,
      lineKey,
      quantity,
      onlyAdded,
      action: 'change',
    });
    const lineItemRow = this.#renderedRows().find((row) => row.dataset.key === lineKey);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * The key of the cart line `element` belongs to.
   * @param {EventTarget | null | undefined} element
   * @returns {string | undefined}
   */
  #rowKeyFor(element) {
    const row = element instanceof Element ? element.closest('[ref="cartItemRows[]"]') : null;
    return row instanceof HTMLElement ? row.dataset.key : undefined;
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   * @param {Event} [event] - The click, whose target identifies the row (SM-776).
   */
  onLineItemRemove(line, event) {
    this.#removeLine(line, this.#rowKeyFor(event?.target));
  }

  /**
   * Removes a line, addressed by key when known.
   * @param {number} line - The line item index, as rendered.
   * @param {string | undefined} lineKey - The line's key, read from its row.
   */
  #removeLine(line, lineKey) {
    const key = lineKey || this.#renderedRows()[line - 1]?.dataset.key;
    // The shopper removed it: stop remembering it, so it isn't restored after the
    // re-render or reported as "sold out and removed" if the bag empties (SM-776).
    if (key) this.#rememberedLines?.forget(key);
    this.updateQuantity({
      line,
      lineKey: key,
      quantity: 0,
      action: 'clear',
    });

    // No key means updateQuantity re-renders an out-of-date bag instead: animate nothing.
    const cartItemRowToRemove = key ? this.#renderedRows().find((row) => row.dataset.key === key) : undefined;

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        const cartDrawerHeading = document.getElementById('cart-drawer-heading');
        const cartDrawerTitleRow = cartDrawerHeading?.closest('.cart-drawer__title-row');
        (cartDrawerTitleRow || cartDrawerHeading)?.remove();
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line, as rendered when the shopper acted.
   * @param {string} [config.lineKey] - The line's key, read from its row when the shopper
   * acted. Preferred over `line`, whose position can be stale (SM-776).
   * @param {number} config.quantity - The quantity.
   * @param {number} [config.onlyAdded] - Set when the request was clamped to the stock left:
   * the line then says "Only N item(s) were added to your cart due to availability".
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const { line, quantity } = config;
    const lineKey = config.lineKey || this.#renderedRows()[line - 1]?.dataset.key || '';

    // Never change.js by position: an unidentified line means a stale bag, so re-render (SM-776).
    if (!lineKey) {
      sectionRenderer
        .renderSection(this.sectionId, { cache: false, mode: this.isDrawer ? 'hydration' : 'full' })
        .catch((error) => {
          if (error?.name !== 'AbortError') console.warn('[cart-items] Re-render of an out-of-date bag failed:', error);
        });
      return;
    }

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();
    this.#pendingQuantityUpdates++;

    const { cartTotal } = this.refs;

    // update.js by key: change.js drops other sold-out lines and renumbers rows. For a
    // logged-in customer's cart Shopify drops them on every write, so cart-remembered-lines.js
    // keeps them shown. Stock: see data-stock-max. Runbook: SM-776.
    const otherLines = this.#snapshotOtherLines(lineKey);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      updates: { [lineKey]: quantity },
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });
    const url = Theme.routes.cart_update_url;

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: lineKey, quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    fetch(url, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        // A partial clamp comes back as a cart with `errors`; an outright rejection
        // (no room at all, or the line itself sold out) as a 422 {status, message, description}.
        const error =
          parsedResponseText.errors ||
          (parsedResponseText.status ? parsedResponseText.description || parsedResponseText.message : null);

        if (error) return this.#handleCartError(line, lineKey, error, otherLines, deferredUpdatePromise);

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: parsedResponseText.sections,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });

        // Awaited, so a morph failure lands in the .catch below instead of going
        // unhandled, and the dropped-line check reads the re-rendered rows.
        await morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
          mode: this.isDrawer ? 'hydration' : 'full',
        });

        this.#updateCartQuantitySelectorButtonStates();
        this.#announceDroppedLines(otherLines);
        if (config.onlyAdded) this.#showLineMessage(lineKey, this.#onlyAddedText(config.onlyAdded), line);
      })
      .catch((error) => {
        console.error(error);
        deferredUpdatePromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Failed to update cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        this.#pendingQuantityUpdates--;
        // Stay locked while another update is still in flight.
        if (this.#pendingQuantityUpdates === 0) this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Records every line except the one being changed (and the lines nested under it),
   * so lines Shopify drops as a side effect of the change can be spotted afterwards.
   * @param {string} lineKey - The key of the line being changed.
   * @returns {Array<{key: string, title: string, html: string}>}
   */
  #snapshotOtherLines(lineKey) {
    return this.#renderedRows()
      .filter((row) => row.dataset.key && row.dataset.key !== lineKey && row.dataset.parentKey !== lineKey)
      .map((row) => ({ key: row.dataset.key ?? '', title: row.dataset.title ?? '', html: row.outerHTML }));
  }

  /**
   * After a re-render: a line the shopper didn't touch that Shopify dropped is kept as
   * sold out, or announced if it can't be (SM-776).
   * @param {Array<{key: string, title: string, html: string}>} otherLines - From `#snapshotOtherLines`.
   */
  async #announceDroppedLines(otherLines) {
    const remainingKeys = new Set(this.#renderedRows().map((row) => row.dataset.key));
    // A remembered sold-out line is still shown (cart-remembered-lines.js), so it isn't "removed".
    const stillShown = this.#rememberedLines?.rememberedKeys() ?? new Set();
    let dropped = otherLines.filter(({ key }) => !remainingKeys.has(key) && !stillShown.has(key));
    if (dropped.length === 0) return;

    // Not remembered yet (it sold out after the bag rendered): restore from the snapshot.
    const notice = this.querySelector('[ref="cartNotice"]');
    try {
      this.#rememberedLinesRequested = true;
      this.#rememberedLinesTrigger?.disconnect();
      const module = this.#rememberedLines ?? (await import('@theme/cart-remembered-lines'));
      this.#rememberedLines = module;
      const restored = await module.restoreDropped(this, dropped, notice?.getAttribute('data-sold-out-text') ?? '');
      dropped = dropped.filter(({ key }) => !restored.has(key));
    } catch (error) {
      console.warn('[cart-items] Could not keep a dropped sold-out line in the bag:', error);
    }
    if (dropped.length === 0) return;

    const template = notice?.getAttribute('data-sold-out-removed') ?? '';
    // A replacer function, so a "$&" or "$'" in a product title is inserted literally.
    this.#showNotice(dropped.map(({ title }) => template.replace('[title]', () => title)).join(' '));
  }

  /**
   * Shows a message in the cart-level notice. Its role="status" region stays rendered;
   * only its content is revealed, so screen readers announce the new text. Queried, not
   * read from refs: refs refresh on a MutationObserver tick, after a morph.
   * @param {string} text
   */
  #showNotice(text) {
    const cartNoticeBody = this.querySelector('[ref="cartNoticeBody"]');
    const cartNoticeText = this.querySelector('[ref="cartNoticeText"]');
    if (!(cartNoticeText instanceof HTMLElement)) return;

    cartNoticeBody?.classList.remove('hidden');
    cartNoticeText.textContent = cartNoticeText.textContent ? `${cartNoticeText.textContent} ${text}` : text;
  }

  /**
   * Shows a message on a line (found by key, else by `line`), or at cart level if the
   * line is gone, so it's never only an event.
   * @param {string | undefined} lineKey
   * @param {string} text
   * @param {number} [line]
   */
  #showLineMessage(lineKey, text, line) {
    const index = lineKey ? this.#renderedRows().findIndex((row) => row.dataset.key === lineKey) : (line ?? 0) - 1;
    const message = this.querySelector(`[ref="cartItemError-${index + 1}"]`);
    const container = this.querySelector(`[ref="cartItemErrorContainer-${index + 1}"]`);
    if (index < 0 || !(message instanceof HTMLElement) || !(container instanceof HTMLElement)) return this.#showNotice(text);
    message.textContent = text;
    container.classList.remove('hidden');
  }

  /**
   * "Only N item(s) were added to your cart due to availability", in the shop's language.
   * @param {number} count
   */
  #onlyAddedText(count) {
    const notice = this.querySelector('[ref="cartNotice"]');
    if (count === 1) return notice?.getAttribute('data-only-added-one') ?? '';
    return (notice?.getAttribute('data-only-added-other') ?? '').replace('999999', String(count));
  }

  /**
   * The cart rows as they are in the DOM right now.
   * @returns {HTMLElement[]}
   */
  #renderedRows() {
    return Array.from(this.querySelectorAll('[ref="cartItemRows[]"]')).filter(
      /** @returns {row is HTMLElement} */ (row) => row instanceof HTMLElement
    );
  }

  /**
   * Handles the cart error.
   *
   * A rejected change may still have changed the cart, so re-render first, then show
   * the error on the line by key (SM-776).
   * @param {number} line - The line, as rendered when the change was made.
   * @param {string} lineKey - The key of the line that was changed.
   * @param {string} error - The error to show on the line.
   * @param {Array<{key: string, title: string, html: string}>} otherLines - From `#snapshotOtherLines`.
   * @param {{ resolve: Function, reject: Function }} deferredUpdatePromise - Resolved with
   * `didError: true` and the fresh cart once re-rendered (as product-form.js does).
   */
  #handleCartError = async (line, lineKey, error, otherLines, deferredUpdatePromise) => {
    const findRowIndex = () =>
      lineKey ? this.#renderedRows().findIndex((row) => row.dataset.key === lineKey) : line - 1;

    try {
      const components = Array.from(document.querySelectorAll('cart-items-component')).filter(
        /** @returns {component is CartItemsComponent} */ (component) => component instanceof CartItemsComponent
      );
      const [cart, ...html] = await Promise.all([
        this.fetchCartData().catch(() => null),
        ...components.map((component) =>
          sectionRenderer.renderSection(component.sectionId, {
            cache: false,
            mode: component.isDrawer ? 'hydration' : 'full',
          })
        ),
      ]);
      this.#updateCartQuantitySelectorButtonStates();
      this.#announceDroppedLines(otherLines);

      const itemCount = parseInt(this.querySelector('[ref="cartItemCount"]')?.textContent ?? '', 10);
      deferredUpdatePromise.resolve({
        cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : undefined,
        detail: {
          sections: Object.fromEntries(components.map((component, index) => [component.sectionId, html[index]])),
          items: cart?.items,
          itemCount: Number.isFinite(itemCount) ? itemCount : undefined,
          source: 'cart-items-component',
          didError: true,
        },
      });
    } catch (renderError) {
      deferredUpdatePromise.reject(new Error(error));

      if (renderError?.name !== 'AbortError') {
        console.warn('[cart-items] Re-render after cart error failed:', renderError);
      }

      // Nothing was re-rendered, so put the line's input back to what the server last said.
      const quantityInput = this.#renderedRows()[findRowIndex()]?.querySelector('input[type="number"]');
      if (quantityInput instanceof HTMLInputElement) quantityInput.value = quantityInput.defaultValue;
    }

    this.#showLineMessage(lineKey, error, line);

    this.dispatchEvent(
      new CartErrorEvent({
        error: error || 'Cart update failed',
        code: 'INVALID',
      })
    );
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    // A cart mutation re-renders this section from the server, so its stock data
    // is fresh again — restart the staleness clock.
    this.#lastValidatedAt = Date.now();

    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer) {
            startViewTransition(() => {
              morphSection(this.sectionId, cartItemsHtml, morphOptions);
            }, ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
